"use client"

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { api } from '../../../services/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Composants
import SearchHeader from './SearchHeader';
import AIAssistantPanel from './AIAssistantPanel';
import SearchFilters from './SearchFilters';
import AdvancedFilters from './AdvancedFilters';
import ResultsList from './ResultsList';
import ResultsGrid from './ResultsGrid';
import MapView from './MapView';
import MoverDetailsModal from './MoverDetailsModal';
import ImageAnalysisModal from './ImageAnalysisModal';
import SearchStats from './SearchStats';
import ViewToggle from './ViewToggle';
import QuickActions from './QuickActions';
import ComparisonPanel from './ComparisonPanel';

// Utilitaires
import { normalizeTruckType, toCanonicalTruckKey, getTruckTypeLabel, formatTND, toBase64, calculateTotalVolume } from './utils/helpers';
import { DEFAULT_RADIUS_KM, DEFAULT_SORT_BY } from './utils/constants';

export default function ClientSearch() {
  // États de géolocalisation
  const [coords, setCoords] = useState(null);
  const [geoError, setGeoError] = useState('');
  const [loading, setLoading] = useState(true);

  // États de recherche
  const [truckType, setTruckType] = useState('');
  const [radiusKm, setRadiusKm] = useState(DEFAULT_RADIUS_KM);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [selectedMover, setSelectedMover] = useState(null);
  
  // États de vue et filtres avancés
  const [viewMode, setViewMode] = useState('list'); // 'list', 'grid', 'map'
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [minPrice, setMinPrice] = useState(null);
  const [maxPrice, setMaxPrice] = useState(null);
  const [minRating, setMinRating] = useState(0);
  
  // États de comparaison
  const [selectedMovers, setSelectedMovers] = useState([]);
  
  // Recherche en temps réel
  const [searchQuery, setSearchQuery] = useState('');

  // États IA
  const [aiOpen, setAiOpen] = useState(false);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);

  // États image
  const [imagePreview, setImagePreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showDetectionFlash, setShowDetectionFlash] = useState(false);
  const [showImagePopup, setShowImagePopup] = useState(false);
  const [popupImage, setPopupImage] = useState(null);
  const [imageAnalysisData, setImageAnalysisData] = useState(null);

  // États meubles
  const [furnitureList, setFurnitureList] = useState([]);
  const [furnitureSource, setFurnitureSource] = useState(null);

  // Effet pour réinitialiser la source quand la liste est vidée
  useEffect(() => {
    if (furnitureList.length === 0) {
      setFurnitureSource(null);
    }
  }, [furnitureList]);

  // Géolocalisation
  const locateMe = useCallback(() => {
    if (!('geolocation' in navigator)) {
      setGeoError("La géolocalisation n'est pas supportée par votre navigateur.");
      return;
    }
    setGeoError('');
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLoading(false);
      },
      () => {
        setGeoError("Impossible de récupérer votre position. Autorisez la localisation.");
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []);

  useEffect(() => {
    locateMe();
  }, [locateMe]);

  // Recherche
  const search = useCallback(async () => {
    if (!coords) {
      toast.warn("Veuillez d'abord autoriser la géolocalisation pour lancer une recherche.");
      return;
    }
    try {
      setLoading(true);
      const resp = await api.searchMovers({
        lat: coords.lat,
        lng: coords.lng,
        radiusKm,
        description: aiInput,
        sortBy,
        ...(truckType && { truckType }),
      });

      let filteredResults = Array.isArray(resp) ? resp : resp?.results || [];

      if (truckType) {
        filteredResults = filteredResults.filter(mover => {
          if (!mover.truckType) return false;
          const normalizedMoverTruck = normalizeTruckType(mover.truckType);
          const normalizedSelectedTruck = normalizeTruckType(truckType);
          return normalizedMoverTruck === normalizedSelectedTruck;
        });
      }

      setResults(filteredResults);
      setFilteredResults(filteredResults);
    } catch (e) {
      console.error("Erreur de l'API de recherche:", e);
      toast.error("Une erreur est survenue lors de la recherche.");
      setResults([]);
      setFilteredResults([]);
    } finally {
      setLoading(false);
    }
  }, [coords, radiusKm, truckType, aiInput, sortBy]);

  // Appliquer les filtres avancés
  const applyFilters = useCallback((resultsToFilter) => {
    let filtered = [...resultsToFilter];

    // Filtre par prix
    if (minPrice !== null) {
      filtered = filtered.filter(r => (r.price || 0) >= minPrice);
    }
    if (maxPrice !== null) {
      filtered = filtered.filter(r => (r.price || 0) <= maxPrice);
    }

    // Filtre par note
    if (minRating > 0) {
      filtered = filtered.filter(r => (r.rating || 0) >= minRating);
    }

    // Recherche textuelle
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        `${r.firstName} ${r.lastName}`.toLowerCase().includes(query) ||
        r.truckType?.toLowerCase().includes(query)
      );
    }

    setFilteredResults(filtered);
  }, [minPrice, maxPrice, minRating, searchQuery]);

  // Appliquer les filtres quand ils changent
  useEffect(() => {
    if (results.length > 0) {
      applyFilters(results);
    }
  }, [minPrice, maxPrice, minRating, searchQuery, results, applyFilters]);

  // Recherche en temps réel avec debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (results.length > 0) {
        applyFilters(results);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, results, applyFilters]);

  // Analyse IA texte
  const handleAiSearch = async () => {
    if (!aiInput.trim()) {
      toast.warn('Veuillez entrer une description de votre déménagement');
      return;
    }

    try {
      setAiLoading(true);
      const response = await api.analyzeSearchQuery(aiInput);

      if (response?.success && response.analysis) {
        setAiSuggestion(response.analysis);

        if (response.analysis.furnitureList?.length > 0) {
          const formattedFurniture = response.analysis.furnitureList.map(item => ({
            ...item,
            width: Number(item.width) || 0,
            depth: Number(item.depth) || 0,
            height: Number(item.height) || 0,
            quantity: Number(item.quantity) || 1,
            confidence: item.confidence ? Math.round(item.confidence * 100) : null
          }));

          setFurnitureList(formattedFurniture);
          setFurnitureSource('detected');

          if (response.analysis.suggestedTruck) {
            const k = toCanonicalTruckKey(response.analysis.suggestedTruck);
            if (k) setTruckType(k);
          }
        }

        toast.success('Analyse terminée avec succès !');
      } else {
        toast.warn('Aucune suggestion disponible pour cette description');
      }
    } catch (error) {
      console.error('Erreur lors de l\'analyse:', error);
      toast.error('Erreur lors de l\'analyse de votre demande');
    } finally {
      setAiLoading(false);
    }
  };

  // Analyse IA image
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setIsAnalyzing(true);
      const imageUrl = URL.createObjectURL(file);
      setImagePreview(imageUrl);
      setPopupImage(imageUrl);
      setShowImagePopup(true);

      const base64Image = await toBase64(file);

      toast.info('Analyse de l\'image en cours...', {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        closeButton: false
      });

      const response = await api.analyzeImage({ image: base64Image });

      toast.dismiss();

      if (response?.analysis?.furnitureList?.length > 0) {
        const furnitureText = response.analysis.furnitureList
          .map(item => `${item.quantity || 1} ${item.name}`)
          .join(', ');
        setAiInput(prev =>
          prev ? `${prev}, ${furnitureText}` : `Je veux déménager: ${furnitureText}`
        );
      }

      if (response.success && response.analysis) {
        // Sauvegarder les données d'analyse
        setImageAnalysisData(response.analysis);
        
        if (response.analysis.detectionImage) {
          setImagePreview(`data:image/jpeg;base64,${response.analysis.detectionImage}`);
        }

        setShowDetectionFlash(true);
        setTimeout(() => setShowDetectionFlash(false), 400);

        // Garder le popup ouvert pour voir les résultats
        // setTimeout(() => {
        //   setShowImagePopup(false);
        //   setPopupImage(null);
        // }, 2000);

        const newFurniture = response.analysis.furnitureList.map(item => ({
          name: item.name,
          width: item.dimensions?.[0] || 0,
          depth: item.dimensions?.[1] || 0,
          height: item.dimensions?.[2] || 0,
          quantity: item.quantity || 1,
          isFragile: item.isFragile || false,
          confidence: item.confidence ? Math.round(item.confidence * 100) : null
        }));

        setFurnitureList(prev => [...prev, ...newFurniture]);

        if (response.analysis.suggestedTruck) {
          const k = toCanonicalTruckKey(response.analysis.suggestedTruck);
          if (k) setTruckType(k);
        }

        toast.success(
          <div>
            <div>✅ {newFurniture.length} meuble(s) détecté(s)</div>
            {response.analysis.suggestedTruck && (
              <div>🚚 Camion recommandé: {getTruckTypeLabel(response.analysis.suggestedTruck)}</div>
            )}
            {response.analysis.price && (
              <div>💵 Estimation: {formatTND(response.analysis.price)}</div>
            )}
          </div>,
          { autoClose: 5000 }
        );
      } else {
        toast.info('Aucun meuble détecté dans l\'image');
      }
    } catch (error) {
      console.error('Erreur lors de l\'analyse de l\'image:', error);
      toast.error(
        <div>
          <div>Erreur lors de l'analyse de l'image</div>
          <div className="text-sm text-gray-300">Veuillez réessayer ou ajouter les meubles manuellement</div>
        </div>
      );
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Mettre à jour la recherche lorsque les filtres changent
  useEffect(() => {
    if (coords && (truckType || aiSuggestion)) {
      const timer = setTimeout(() => {
        search();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [truckType, radiusKm, sortBy, coords, aiSuggestion, search]);

  // Fonctions de comparaison
  const toggleSelectMover = useCallback((mover) => {
    setSelectedMovers(prev => {
      const exists = prev.some(m => m.id === mover.id);
      if (exists) {
        return prev.filter(m => m.id !== mover.id);
      } else {
        return [...prev, mover];
      }
    });
  }, []);

  const removeFromComparison = useCallback((moverId) => {
    setSelectedMovers(prev => prev.filter(m => m.id !== moverId));
  }, []);

  const clearComparison = useCallback(() => {
    setSelectedMovers([]);
  }, []);

  const handleCompare = useCallback(() => {
    if (selectedMovers.length < 2) {
      toast.warn('Sélectionnez au moins 2 déménageurs pour comparer');
      return;
    }
    // TODO: Ouvrir modal de comparaison détaillée
    toast.info(`Comparaison de ${selectedMovers.length} déménageurs`);
  }, [selectedMovers]);

  // Fonctions d'action rapide
  const handleExport = useCallback(() => {
    const dataStr = JSON.stringify(filteredResults, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `demenageurs-${new Date().toISOString()}.json`;
    link.click();
    toast.success('Résultats exportés');
  }, [filteredResults]);

  const handleShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Résultats de recherche',
          text: `J'ai trouvé ${filteredResults.length} déménageur(s)`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Erreur de partage:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Lien copié dans le presse-papiers');
    }
  }, [filteredResults]);

  const handleSaveFavorite = useCallback(() => {
    const searchData = {
      coords,
      radiusKm,
      truckType,
      sortBy,
      timestamp: new Date().toISOString()
    };
    const saved = JSON.parse(localStorage.getItem('favoriteSearches') || '[]');
    saved.push(searchData);
    localStorage.setItem('favoriteSearches', JSON.stringify(saved));
    toast.success('Recherche sauvegardée');
  }, [coords, radiusKm, truckType, sortBy]);

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <SearchHeader 
          onRefreshLocation={locateMe}
          isLocating={loading}
        />

        <AIAssistantPanel
          isOpen={aiOpen}
          onToggle={() => setAiOpen(!aiOpen)}
          aiInput={aiInput}
          onInputChange={setAiInput}
          aiLoading={aiLoading}
          isAnalyzing={isAnalyzing}
          onAnalyzeText={handleAiSearch}
          onImageUpload={handleImageUpload}
          imagePreview={imagePreview}
          showDetectionFlash={showDetectionFlash}
          detectionData={imageAnalysisData}
          onRemoveImage={() => {
            setImagePreview(null);
            setImageAnalysisData(null);
            if (imagePreview) URL.revokeObjectURL(imagePreview);
          }}
          aiSuggestion={aiSuggestion}
          formatTND={formatTND}
        />

        <SearchFilters
          coords={coords}
          geoError={geoError}
          sortBy={sortBy}
          onSortChange={setSortBy}
          truckType={truckType}
          onTruckTypeChange={setTruckType}
          radiusKm={radiusKm}
          onRadiusChange={setRadiusKm}
          onSearch={search}
          loading={loading}
        />

        {/* Filtres avancés */}
        <div className="flex items-center gap-4 flex-wrap">
          <AdvancedFilters
            isOpen={advancedFiltersOpen}
            onToggle={() => setAdvancedFiltersOpen(!advancedFiltersOpen)}
            minPrice={minPrice}
            maxPrice={maxPrice}
            minRating={minRating}
            onMinPriceChange={setMinPrice}
            onMaxPriceChange={setMaxPrice}
            onMinRatingChange={setMinRating}
            onReset={() => {
              setMinPrice(null);
              setMaxPrice(null);
              setMinRating(0);
            }}
          />

          {/* Recherche textuelle */}
          <div className="flex-1 min-w-[200px] max-w-md">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un déménageur..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>
        </div>

        {/* Statistiques */}
        {filteredResults.length > 0 && (
          <SearchStats results={filteredResults} radiusKm={radiusKm} />
        )}

        {/* Barre d'outils */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-bold text-white">
              {loading ? 'Recherche...' : `${filteredResults.length} résultat${filteredResults.length > 1 ? 's' : ''}`}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <ViewToggle viewMode={viewMode} onViewChange={setViewMode} />
            <QuickActions
              onExport={handleExport}
              onShare={handleShare}
              onSaveFavorite={handleSaveFavorite}
              onRefresh={search}
            />
          </div>
        </div>

        {/* Affichage des résultats selon le mode */}
        {viewMode === 'map' ? (
          <MapView
            coords={coords}
            results={filteredResults}
            radiusKm={radiusKm}
            geoError={geoError}
            onSelectMover={setSelectedMover}
          />
        ) : viewMode === 'grid' ? (
          <ResultsGrid
            results={filteredResults}
            loading={loading}
            onSelectMover={setSelectedMover}
            selectedMovers={selectedMovers}
            onToggleSelect={toggleSelectMover}
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <ResultsList
              results={filteredResults}
              loading={loading}
              onSelectMover={setSelectedMover}
              selectedMovers={selectedMovers}
              onToggleSelect={toggleSelectMover}
            />
            <MapView
              coords={coords}
              results={filteredResults}
              radiusKm={radiusKm}
              geoError={geoError}
              onSelectMover={setSelectedMover}
            />
          </div>
        )}
      </div>

      <MoverDetailsModal
        selectedMover={selectedMover}
        onClose={() => setSelectedMover(null)}
        furnitureList={furnitureList}
      />

      <ImageAnalysisModal
        isOpen={showImagePopup}
        imageUrl={popupImage}
        analysisData={imageAnalysisData}
        isAnalyzing={isAnalyzing}
        onClose={() => {
          setShowImagePopup(false);
          setPopupImage(null);
          setImageAnalysisData(null);
        }}
      />

      {/* Panneau de comparaison */}
      <ComparisonPanel
        selectedMovers={selectedMovers}
        onRemove={removeFromComparison}
        onClear={clearComparison}
        onCompare={handleCompare}
      />
    </div>
  );
}




